import { useMemo, useState } from "react";
import useSWR from "swr";
import { fetcher } from "../../../../../services/fetcher";
import DataTable from "../../../../../common/components/DataTable/DataTable";
import { AssignCover_API } from "../../../../../api/endpoints";
import CoverBrochureDrawer from "../../../components/CoverBrochureDrawer/CoverBrochureDrawer";
import {
  Badge,
  Card,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";

const priceOptions = [
  { value: "all", label: "All Prices" },
  { value: "below_100", label: "Below ₹100" },
  { value: "100_to_199", label: "₹100 - ₹199" },
  { value: "200_plus", label: "₹200+" },
];

const getActiveCount = (covers) => {
  if (!covers.length) return 0;
  const active = covers.filter(
    (cover) =>
      cover?.is_active === true ||
      String(cover?.status || "").toLowerCase() === "active"
  );
  return active.length || covers.length;
};

const getScopeCount = (covers, scope) =>
  covers.filter((cover) =>
    String(cover?.cover_scope || "").toLowerCase().includes(scope.toLowerCase())
  ).length;

const OperatorAssignCover = () => {
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [activeCoverId, setActiveCoverId] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedScope, setSelectedScope] = useState("all");
  const [selectedPrice, setSelectedPrice] = useState("all");

  const { data, error, isLoading } = useSWR(AssignCover_API, fetcher);

  const assignedCovers = useMemo(
    () => (Array.isArray(data?.customize_covers) ? data.customize_covers : []),
    [data]
  );

  const scopeOptions = useMemo(() => {
    const scopes = Array.from(
      new Set(
        assignedCovers
          .map((cover) => String(cover?.cover_scope || "").trim())
          .filter(Boolean)
      )
    );

    return [
      { value: "all", label: "All Categories" },
      ...scopes.map((scope) => ({ value: scope, label: scope })),
    ];
  }, [assignedCovers]);

  const filteredCovers = useMemo(() => {
    return assignedCovers.filter((cover) => {
      const title = String(cover?.title || "").toLowerCase();
      const scope = String(cover?.cover_scope || "").toLowerCase();
      const price = Number(cover?.price);

      const matchesSearch =
        !searchValue || title.includes(searchValue.toLowerCase());
      const matchesScope =
        selectedScope === "all" || scope === selectedScope.toLowerCase();
      const matchesPrice =
        selectedPrice === "all" ||
        (selectedPrice === "below_100" && Number.isFinite(price) && price < 100) ||
        (selectedPrice === "100_to_199" && Number.isFinite(price) && price >= 100 && price < 200) ||
        (selectedPrice === "200_plus" && Number.isFinite(price) && price >= 200);

      return matchesSearch && matchesScope && matchesPrice;
    });
  }, [assignedCovers, searchValue, selectedScope, selectedPrice]);

  const totalAssigned = assignedCovers.length;
  const activeCovers = getActiveCount(assignedCovers);
  const domesticCovers = getScopeCount(assignedCovers, "domestic");
  const internationalCovers = getScopeCount(assignedCovers, "international");

  const plansConfig = {
    fields: [
      { key: "title", label: "Name" },
      { key: "cover_scope", label: "Scope" },
      { key: "currency", label: "Currency" },
      { key: "price", label: "Price" },
    ],
    selectionCheckbox: true,
    doubleClickRow: true,
    onRowClick: (row) => {
      if (!row?.id) return;
      setActiveCoverId(row.id);
      setDrawerOpened(true);
    },
    searchPlaceholder: "Search covers by name...",
    searchInputWidth: 320,
    toolbarLeftContent: (
      <Group spacing="sm" wrap="wrap" sx={{ flex: 1 }}>
        <Select
          data={scopeOptions}
          value={selectedScope}
          onChange={(value) => setSelectedScope(value ?? "all")}
          searchable
          nothingFound="No categories"
          styles={{ input: { minWidth: 180 } }}
        />

        <Select
          data={priceOptions}
          value={selectedPrice}
          onChange={(value) => setSelectedPrice(value ?? "all")}
          styles={{ input: { minWidth: 180 } }}
        />
      </Group>
    ),
    toolbarNoWrap: true,
    showFilterButton: false,
  };

  if (isLoading) return <Text>Loading...</Text>;
  if (error) return <Text color="red">Error loading assign plan data</Text>;

  return (
    <>
      <CoverBrochureDrawer
        coverId={activeCoverId}
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
      />

      <Stack spacing="lg">
        <Stack spacing={4}>
          <Text fz="xl" fw={700}>
            Your Plans
          </Text>
          <Text c="dimmed">Manage assigned covers and review plan details.</Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
          <Card withBorder radius="md" p="md">
            <Group position="apart" align="flex-start">
              <Text c="dimmed" fz="xs" tt="uppercase" fw={700}>
                Total Assigned Covers
              </Text>
              <Badge variant="light" color="blue">
                {totalAssigned}
              </Badge>
            </Group>
            <Text fz="xl" fw={700} mt="md">
              {totalAssigned}
            </Text>
          </Card>

          <Card withBorder radius="md" p="md">
            <Group position="apart" align="flex-start">
              <Text c="dimmed" fz="xs" tt="uppercase" fw={700}>
                Active Covers
              </Text>
              <Badge variant="light" color="green">
                {activeCovers}
              </Badge>
            </Group>
            <Text fz="xl" fw={700} mt="md">
              {activeCovers}
            </Text>
          </Card>

          <Card withBorder radius="md" p="md">
            <Group position="apart" align="flex-start">
              <Text c="dimmed" fz="xs" tt="uppercase" fw={700}>
                Domestic
              </Text>
              <Badge variant="light" color="blue">
                {domesticCovers}
              </Badge>
            </Group>
            <Text fz="xl" fw={700} mt="md">
              {domesticCovers}
            </Text>
          </Card>

          <Card withBorder radius="md" p="md">
            <Group position="apart" align="flex-start">
              <Text c="dimmed" fz="xs" tt="uppercase" fw={700}>
                International
              </Text>
              <Badge variant="light" color="grape">
                {internationalCovers}
              </Badge>
            </Group>
            <Text fz="xl" fw={700} mt="md">
              {internationalCovers}
            </Text>
          </Card>
        </SimpleGrid>

        <Paper withBorder radius="md" p="md">
          <Group position="apart" align="center" wrap="wrap" spacing="sm">
            <Text fw={700}>Assigned Covers</Text>
            <Text c="dimmed">Showing {filteredCovers.length} of {totalAssigned} covers</Text>
          </Group>
        </Paper>

        <DataTable
          config={plansConfig}
          data={filteredCovers}
          searchValue={searchValue}
          onSearch={(event) => setSearchValue(event.currentTarget.value)}
          loading={isLoading}
        />
      </Stack>
    </>
  );
};

export default OperatorAssignCover;
